/**
 * `useFimService` — the ACT side of the FIM UX.
 *
 * POSTs to the `fim-service` (base URL `FIM_SERVICE_URL` from runtime config;
 * local override via `OVERWRITE_CONFIG=true FIM_SERVICE_URL=...`). The
 * service owns all `fim-*` writes; Forecast reads the resulting entries back
 * from Rolodex (see `hooks.ts`).
 *
 * Action contract (SEAM #2): served under `/api/v1/sites/{site}` — site is a
 * REQUIRED path param (the service dropped organization tenancy; site is the
 * deterministic partition).
 *   POST   /api/v1/sites/{site}/sources         (multipart) → create source list
 *   DELETE /api/v1/sites/{site}/sources/{id}    → remove list + records
 *   GET    /api/v1/sites/{site}/sources/corpora → adapter corpora
 *   POST   /api/v1/sites/{site}/fusion/run      → run the fusion engine
 *   POST   /api/v1/sites/{site}/matching/run    → standalone time series linkage
 *   POST   /api/v1/sites/{site}/review          → durable review + constraints
 *   GET    /api/v1/sites/{site}/config          → tag structure + fusion mode
 *   POST   /api/v1/sites/{site}/subjects/{uid}/instantiate → create Asset Instance
 *   POST   /api/v1/sites/{site}/instances/{uid}/refill     → re-queue its fill
 *   GET    /api/v1/sites/{site}/instances                  → reconciling instance read
 *   POST   /api/v1/sites/{site}/evaluation/source-list-compare → score one
 *          docnav_bbox source list against another as ground truth
 */

import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { isAxiosError } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { useCallback, useMemo } from 'react'

import type {
  AgentEdgePolicy,
  AssetInstanceFillStatus,
  FimAdapter,
  FimAssetInstanceEntry,
  FimInstanceListOptions,
  MatchRunSummary,
  ReviewAction,
  SiteConfig,
  SubjectKind,
  TierConfig,
} from './types'

const API_PREFIX = '/api/v1'
const REVIEW_REQUEST_TIMEOUT_MS = 60_000

export interface CreateSourceListForm {
  adapter: FimAdapter
  name: string
  /** Adapter-specific params (serialised to `params_json`). */
  params?: Record<string, unknown>
  /** CSV file for the csv adapters (`poc_csv`, `cmms_csv`). */
  file?: File | null
  site?: string
  organization?: string
}

export interface SourceListSummary {
  id: string
  name: string
  adapter: string
  site: string
  record_count: number
  created_at?: string | null
  updated_at?: string | null
  params: Record<string, unknown>
}

export interface RunFusionRequest {
  source_list_ids: string[]
  site?: string
  organization?: string
  /** `reuse` replays stored agent edges (0 LLM); `regenerate` re-runs the agent. */
  agent_edges?: AgentEdgePolicy
  /** Which escalation tiers to execute this run (tiers 2-4). Omit ⇒ none run. */
  tiers?: TierConfig
}

/**
 * Kickoff ack for `POST /fusion/run`. The run is async: the service returns as
 * soon as the run is queued (`status: 'running'`, counts empty/absent). Live
 * progress and final status/counts land on the `fim-fusion-run` entry — read
 * them back via `useFusionRunStatus` / `useFimFusionRuns`, not from this
 * response. Tolerates the pre-async backend that returned `status: 'done'` with
 * populated counts synchronously.
 */
export interface FusionRunSummary {
  run_id: string
  status: string
  counts?: Record<string, number | string>
}

/** One decision inside a batch review submission. */
export interface ReviewDecision {
  action: ReviewAction
  alias_keys: string[]
  target_subject_uid?: string
  expected_revision?: string
  canonical_name?: string
  kind?: SubjectKind
  source_record_ids?: string[]
  exists?: boolean
  type?: string | null
  reason?: string
  /**
   * Review kind (architecture §8): `existence` (default — identity; emits
   * constraints + applies) vs the template-validation kinds `doc_binding` /
   * `tag_binding` / `tag_desc` (audit-only; never touch clustering).
   */
  review_kind?: 'existence' | 'doc_binding' | 'tag_binding' | 'tag_desc'
  // binding payload (kinds #2/#4)
  template_id?: string
  slot_key?: string
  to_ref?: string
  to_subject_uid?: string
  // mark_variant payload; alias_keys is derived server-side.
  variant_norm?: string
  corrected_tag?: string
}

export interface SubmitReviewRequest {
  action?: ReviewAction
  /** Normalized aliases this decision is about (single-decision form). */
  alias_keys?: string[]
  target_subject_uid?: string
  expected_revision?: string
  canonical_name?: string
  kind?: SubjectKind
  source_record_ids?: string[]
  exists?: boolean
  type?: string | null
  reason?: string
  review_kind?: 'existence' | 'doc_binding' | 'tag_binding' | 'tag_desc'
  template_id?: string
  slot_key?: string
  to_ref?: string
  to_subject_uid?: string
  /** mark_variant payload (single-decision form); alias_keys derived server-side. */
  variant_norm?: string
  corrected_tag?: string
  /** Batch form: many decisions applied in ONE incremental pass. */
  decisions?: ReviewDecision[]
  reviewer: string
  site?: string
  organization?: string
  /**
   * How the decision lands on the projections: 'targeted' (default —
   * incremental re-fusion of only the touched subjects, seconds), 'full'
   * (whole-site re-fuse), 'none' (durable record only; next run applies).
   */
  apply?: 'none' | 'targeted' | 'full'
}

export interface SubmitReviewResult {
  review_id: string
  review_ids?: string[]
  constraints_written: number
  refused: boolean
  /** What actually happened to the projections: none | targeted | full. */
  applied?: string
  /** fim-subject entry ids rewritten by a targeted apply. */
  affected_subject_ids?: string[]
}

interface SubjectDecisionRequest {
  site: string
  subjectUid: string
  reviewer: string
  aliasKeys: string[]
  expectedRevision?: string
  reason?: string
}

/** Validate the exact subject projection currently shown to the reviewer. */
export type ValidateSubjectRequest = SubjectDecisionRequest

/** Persist canonical subject fields without conflating them with identity. */
export interface ReclassifySubjectRequest extends SubjectDecisionRequest {
  canonicalName: string
  kind: SubjectKind
  type: string | null
}

/** Remove meaningless evidence before it can participate in clustering. */
export interface SuppressSubjectInputRequest extends SubjectDecisionRequest {
  sourceRecordIds?: string[]
}

export interface SubjectDecisionCanonical {
  name: string
  kind: SubjectKind
  type: string | null
}

export interface MergeSubjectsDecision {
  operation: 'merge_subjects'
  subject_uids: string[]
  survivor_subject_uid: string
  expected_revisions: Record<string, string>
  canonical: SubjectDecisionCanonical
  reason?: string
}

export interface SplitSubjectPartition {
  partition_key: string
  source_record_ids: string[]
  merge_into_subject_uid?: string
  merge_into_expected_revision?: string
  canonical?: Partial<SubjectDecisionCanonical>
}

export interface SplitSubjectDecision {
  operation: 'split_subject'
  subject_uid: string
  expected_revision: string
  partitions: SplitSubjectPartition[]
  reason?: string
}

export interface CorrectOcrDecision {
  operation: 'correct_ocr'
  subject_uid: string
  expected_revision: string
  source_record_id: string
  observed_value: string
  corrected_value: string
  reason?: string
}

export interface RevertSubjectDecision {
  operation: 'revert_decision'
  decision_id: string
  expected_revisions: Record<string, string>
  reason?: string
}

export type SubjectStructuralDecision =
  | MergeSubjectsDecision
  | SplitSubjectDecision
  | CorrectOcrDecision
  | RevertSubjectDecision

export interface SubjectDecisionSnapshot {
  subject_uid: string
  entry_id: string
  revision: string
  name: string
  kind: SubjectKind
  type: string | null
  aliases: Array<
    | string
    | {
        value?: string
        norm?: string
        kind?: string
        precision?: string
      }
  >
  source_record_ids: string[]
  validation_status: 'unvalidated' | 'validated' | 'stale'
}

export interface SubjectDecisionPreview {
  operation: SubjectStructuralDecision['operation']
  before_subjects: SubjectDecisionSnapshot[]
  after_subjects: SubjectDecisionSnapshot[]
  predicted_result_subject_ids: string[]
  affected_source_record_ids: string[]
  supersede_redirects: Record<string, string>
  warnings: string[]
}

export interface PreviewSubjectDecisionRequest {
  site: string
  decision: SubjectStructuralDecision
}

export interface CommitSubjectDecisionRequest extends PreviewSubjectDecisionRequest {
  idempotencyKey: string
  reviewer: string
}

export interface SubjectDecisionCommitResult {
  decision_id: string
  idempotency_key: string
  applied: boolean
  predicted_result_subject_ids: string[]
  actual_result_subject_ids: string[]
  supersede_redirects: Record<string, string>
  preview: SubjectDecisionPreview
}

export interface SearchSubjectDecisionsRequest {
  site: string
  query: string
  limit?: number
  excludeSubjectUid?: string
}

export interface SearchSubjectDecisionsResult {
  subjects: SubjectDecisionSnapshot[]
  total_matches: number
  limit: number
}

export interface SubjectDecisionRevertContext {
  decision_id: string
  operation: SubjectStructuralDecision['operation']
  active: boolean
  affected_source_record_ids: string[]
  subjects: SubjectDecisionSnapshot[]
  expected_revisions: Record<string, string>
}

export interface CorpusOption {
  value: string
  label: string
  count?: number | null
}

export interface CorporaResponse {
  /** params_json key the chosen corpus value maps to ('' = adapter has no corpus). */
  param: string
  options: CorpusOption[]
}

export interface DeleteSourceListResult {
  deleted: number
  source_records: number
}

// ---------------------------------------------------------------------------
// Source-list evaluation (`POST /sites/{site}/evaluation/source-list-compare`).
// Hand-mirrors fim-service `fim_service/evaluation/compare.py` +
// `fim_service/api/evaluation_api.py` — keep in sync by hand.
// ---------------------------------------------------------------------------

export interface CompareSourceListsRequest {
  site?: string
  gtSourceListId: string
  candidateSourceListId: string
  /** IoU at/above which a box pair counts as localized (default 0.5). */
  boxIouThreshold?: number
  /** Max center distance (page fractions) for the proximity stage that
   * classifies a detected-but-misplaced box as mislocalized (default 0.15). */
  boxProximityCap?: number
  /** false drops is_citation=true references ("SEE DWG" notes) from both sides. */
  includeCitations?: boolean
  /** Restrict the whole comparison to documents BOTH lists cover (backend
   * default true), so a corpus gap doesn't inflate misses/spurious. */
  sharedDocumentsOnly?: boolean
  maxAliasDetail?: number
}

/** Match counts at one level; rates are null when the denominator is 0. */
export interface EvalLevelMetrics {
  matched: number
  missed: number
  spurious: number
  recall: number | null
  precision: number | null
  f1: number | null
}

/** Box-level aggregates over MATCHED page tuples only. */
export interface EvalBoxMetrics {
  gt_boxes: number
  candidate_boxes: number
  localized: number
  mislocalized: number
  missed: number
  spurious: number
  detection_rate: number | null
  localization_rate: number | null
  mean_iou: number | null
  mean_iou_localized: number | null
  mean_center_offset_mislocalized: number | null
}

export interface EvalBoxPair {
  gt_box: string
  candidate_box: string
  iou: number
  center_distance: number
}

/** Box-level problems inside one matched (norm, document, page) tuple. */
export interface EvalMatchedPageDetail {
  document_id: string
  page: number
  gt_box_count: number
  candidate_box_count: number
  localized: number
  mislocalized: EvalBoxPair[]
  missed_boxes: string[]
  spurious_boxes: string[]
  mean_iou: number | null
}

/** An unmatched (norm, document, page) tuple, with its boxes for the viewer. */
export interface EvalPageOccurrence {
  document_id: string
  page: number
  boxes: string[]
  citation_only: boolean | null
}

export type EvalAliasStatus = 'missed' | 'spurious' | 'partial' | 'perfect'

export interface EvalAliasRow {
  norm: string
  value: string
  gt_kind: string | null
  candidate_kind: string | null
  kind_mismatch: boolean
  status: EvalAliasStatus
  gt_page_count: number
  candidate_page_count: number
  matched_page_count: number
  boxes: EvalBoxMetrics
  missed_pages: EvalPageOccurrence[]
  spurious_pages: EvalPageOccurrence[]
  page_details: EvalMatchedPageDetail[]
}

export interface EvalDocumentRow {
  document_id: string
  gt_pages: number
  candidate_pages: number
  matched_pages: number
  missed_pages: number
  spurious_pages: number
  page_recall: number | null
}

/** Corpus-mismatch guard: page metrics restricted to shared documents. */
export interface EvalDocumentCoverage {
  shared: number
  gt_only: string[]
  candidate_only: string[]
  pages_shared_docs: EvalLevelMetrics
}

export interface EvalSideCounts {
  records: number
  norms: number
  page_tuples: number
  boxes: number
  skipped_references: number
  excluded_citation_references: number
  /** Page tuples dropped by shared_documents_only (document covered by only
   * one of the two lists). */
  out_of_shared_docs_tuples: number
}

export interface EvalComparisonResult {
  gt_counts: EvalSideCounts
  candidate_counts: EvalSideCounts
  aliases: EvalLevelMetrics
  pages: EvalLevelMetrics
  boxes: EvalBoxMetrics
  documents: EvalDocumentCoverage
  document_rows: EvalDocumentRow[]
  /** Worst-first; may be truncated to `max_alias_detail`. */
  alias_rows: EvalAliasRow[]
  total_alias_rows: number
}

export interface EvalSourceListRef {
  id: string
  name: string
  adapter: string
  record_count: number
  created_at?: string | null
}

export interface CompareSourceListsResponse {
  site: string
  gt: EvalSourceListRef
  candidate: EvalSourceListRef
  box_iou_threshold: number
  box_proximity_cap: number
  include_citations: boolean
  shared_documents_only: boolean
  result: EvalComparisonResult
}

/** Result of instantiate/refill: the instance entry + its queued lifecycle. */
export interface AssetInstanceActionResult {
  instance_id: string
  fill_status: AssetInstanceFillStatus
}

/** `GET /sites/{site}/instances` — fim-service reconciles staleness on the way
 * out (settled instances whose subject/template moved on come back `stale`). */
export interface InstanceListResult {
  instances: FimAssetInstanceEntry[]
  stale_marked: number
}

/**
 * True when instantiate/refill was REFUSED because a fill is already queued or
 * running for the instance (fill-ownership protocol v2 §3) — a guard, not a
 * failure. (fim-service also 409s instantiate for unvalidated / non-
 * instantiable subjects; `fillActionErrorMessage` surfaces the detail either
 * way.)
 */
export const isFillConflict = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409

/**
 * Human-readable message for a failed instantiate/refill: the service's
 * `detail` when it sent one, a friendly fill-already-in-progress line for a
 * bare 409, otherwise the raw error.
 */
export const fillActionErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)
      ?.detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (error.response?.status === 409) {
      return t`A fill is already in progress for this asset — wait for it to finish, then try again.`
    }
  }
  return error instanceof Error ? error.message : String(error)
}

export function useFimService() {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()

  const baseURL = useMemo(
    () => `${FIM_SERVICE_URL}${API_PREFIX}`,
    [FIM_SERVICE_URL]
  )

  /** `/api/v1/sites/{site}` — every action is site-scoped (required path param). */
  const siteBase = useCallback(
    (site?: string): string => {
      if (!site) {
        throw new Error('fim-service action requires a site (path param)')
      }
      return `${baseURL}/sites/${encodeURIComponent(site)}`
    },
    [baseURL]
  )

  /** POST /sites/{site}/sources — multipart (adapter + name + optional CSV). */
  const createSourceList = useCallback(
    async (form: CreateSourceListForm): Promise<SourceListSummary> => {
      const body = new FormData()
      body.append('adapter', form.adapter)
      body.append('name', form.name)
      body.append('params_json', JSON.stringify(form.params ?? {}))
      if (form.file) body.append('file', form.file)

      const res = await authorizedAxios.post(
        `${siteBase(form.site)}/sources`,
        body,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return res.data as SourceListSummary
    },
    [authorizedAxios, siteBase]
  )

  /** POST /sites/{site}/fusion/run — reconcile the selected source lists. */
  const runFusion = useCallback(
    async (req: RunFusionRequest): Promise<FusionRunSummary> => {
      const { site, organization, ...payload } = req
      void organization
      const res = await authorizedAxios.post(
        `${siteBase(site)}/fusion/run`,
        payload
      )
      return res.data as FusionRunSummary
    },
    [authorizedAxios, siteBase]
  )

  /**
   * POST /sites/{site}/matching/run — standalone time series linkage: the
   * fusion datapoint matcher over the latest run's subjects, without a
   * fusion. Async ack; progress lands on the run record (`useMatchingRuns`).
   * `llmBinding` opts the run into the LLM adjudication pass over near-miss
   * candidates (omitted from the body when off; the backend owns the cap).
   */
  const runMatching = useCallback(
    async (site?: string, llmBinding?: boolean): Promise<MatchRunSummary> => {
      const res = await authorizedAxios.post(
        `${siteBase(site)}/matching/run`,
        llmBinding ? { llm_binding: true } : undefined
      )
      return res.data as MatchRunSummary
    },
    [authorizedAxios, siteBase]
  )

  /** POST /sites/{site}/review — write the durable human decision (+ constraints). */
  const submitReview = useCallback(
    async (req: SubmitReviewRequest): Promise<SubmitReviewResult> => {
      const { site, organization, ...payload } = req
      void organization
      const res = await authorizedAxios.post(
        `${siteBase(site)}/review`,
        payload,
        { timeout: REVIEW_REQUEST_TIMEOUT_MS }
      )
      return res.data as SubmitReviewResult
    },
    [authorizedAxios, siteBase]
  )

  const validateSubject = useCallback(
    (req: ValidateSubjectRequest): Promise<SubmitReviewResult> =>
      submitReview({
        action: 'accept',
        alias_keys: req.aliasKeys,
        target_subject_uid: req.subjectUid,
        expected_revision: req.expectedRevision,
        reason: req.reason,
        reviewer: req.reviewer,
        site: req.site,
      }),
    [submitReview]
  )

  const reclassifySubject = useCallback(
    (req: ReclassifySubjectRequest): Promise<SubmitReviewResult> =>
      submitReview({
        action: 'edit',
        alias_keys: req.aliasKeys,
        target_subject_uid: req.subjectUid,
        expected_revision: req.expectedRevision,
        canonical_name: req.canonicalName,
        kind: req.kind,
        type: req.type,
        reason: req.reason,
        reviewer: req.reviewer,
        site: req.site,
      }),
    [submitReview]
  )

  const suppressSubjectInput = useCallback(
    (req: SuppressSubjectInputRequest): Promise<SubmitReviewResult> =>
      submitReview({
        action: 'suppress',
        alias_keys: req.aliasKeys,
        source_record_ids: req.sourceRecordIds,
        target_subject_uid: req.subjectUid,
        expected_revision: req.expectedRevision,
        reason: req.reason,
        reviewer: req.reviewer,
        site: req.site,
      }),
    [submitReview]
  )

  const previewSubjectDecision = useCallback(
    async (
      req: PreviewSubjectDecisionRequest
    ): Promise<SubjectDecisionPreview> => {
      const res = await authorizedAxios.post(
        `${siteBase(req.site)}/subject-decisions/preview`,
        { decision: req.decision }
      )
      return res.data as SubjectDecisionPreview
    },
    [authorizedAxios, siteBase]
  )

  const commitSubjectDecision = useCallback(
    async (
      req: CommitSubjectDecisionRequest
    ): Promise<SubjectDecisionCommitResult> => {
      const res = await authorizedAxios.post(
        `${siteBase(req.site)}/subject-decisions/commit`,
        {
          idempotency_key: req.idempotencyKey,
          reviewer: req.reviewer,
          decision: req.decision,
        }
      )
      return res.data as SubjectDecisionCommitResult
    },
    [authorizedAxios, siteBase]
  )

  const searchSubjectDecisionCandidates = useCallback(
    async (
      req: SearchSubjectDecisionsRequest
    ): Promise<SearchSubjectDecisionsResult> => {
      const res = await authorizedAxios.get(
        `${siteBase(req.site)}/subject-decisions/subjects`,
        {
          params: {
            q: req.query,
            limit: req.limit ?? 20,
            exclude_uid: req.excludeSubjectUid,
          },
        }
      )
      return res.data as SearchSubjectDecisionsResult
    },
    [authorizedAxios, siteBase]
  )

  const getSubjectDecisionRevertContext = useCallback(
    async (
      site: string,
      decisionId: string
    ): Promise<SubjectDecisionRevertContext> => {
      const res = await authorizedAxios.get(
        `${siteBase(site)}/subject-decisions/${encodeURIComponent(decisionId)}/revert-context`
      )
      return res.data as SubjectDecisionRevertContext
    },
    [authorizedAxios, siteBase]
  )

  /** GET /sites/{site}/config — the site's tag structure + derived fusion mode. */
  const getSiteConfig = useCallback(
    async (site?: string): Promise<SiteConfig> => {
      const res = await authorizedAxios.get(`${siteBase(site)}/config`)
      return res.data as SiteConfig
    },
    [authorizedAxios, siteBase]
  )

  /** GET /sites/{site}/sources/corpora — selectable corpora for an adapter. */
  const getCorpora = useCallback(
    async (
      adapter: FimAdapter,
      site?: string,
      organization?: string
    ): Promise<CorporaResponse> => {
      void organization // tenancy is site-only; kept for caller compatibility
      const res = await authorizedAxios.get(
        `${siteBase(site)}/sources/corpora`,
        { params: { adapter } }
      )
      return res.data as CorporaResponse
    },
    [authorizedAxios, siteBase]
  )

  /**
   * POST /sites/{site}/subjects/{subject_uid}/instantiate — explicitly promote
   * a VALIDATED Subject into a `fim-asset-instance` entry. Creates the
   * instance (`fill_status` queued / missing_type / no_template) and
   * fire-and-forget triggers the llm-service async fill.
   */
  const instantiateSubject = useCallback(
    async (
      site: string,
      subjectUid: string
    ): Promise<AssetInstanceActionResult> => {
      const res = await authorizedAxios.post(
        `${siteBase(site)}/subjects/${encodeURIComponent(subjectUid)}/instantiate`
      )
      return res.data as AssetInstanceActionResult
    },
    [authorizedAxios, siteBase]
  )

  /**
   * GET /sites/{site}/instances — the RECONCILING instance read: fim-service
   * re-derives staleness server-side while listing (fill-ownership protocol
   * v2 §10), and the catalog's load path for the entries themselves.
   *
   * `shape=summary` trims each instance's `fill` to lifecycle plus a slot
   * tally: full instances are 16.4MB for longridge's 269, 98% of it inside
   * `fill`, against a list that renders a status, a template and "n/m".
   * `reconcile: false` skips the staleness pass, which costs a second or
   * more and has no business gating a paint.
   */
  const listInstances = useCallback(
    async (
      site: string,
      { shape = 'full', reconcile = true }: FimInstanceListOptions = {}
    ): Promise<InstanceListResult> => {
      const res = await authorizedAxios.get(
        `${siteBase(site)}/instances?shape=${shape}&reconcile=${reconcile}`
      )
      return res.data as InstanceListResult
    },
    [authorizedAxios, siteBase]
  )

  /** GET /sites/{site}/instances/{subject_uid} — one instance at FULL shape,
   * for the detail panel that renders slots. */
  const getInstance = useCallback(
    async (
      site: string,
      subjectUid: string
    ): Promise<FimAssetInstanceEntry> => {
      const res = await authorizedAxios.get(
        `${siteBase(site)}/instances/${encodeURIComponent(subjectUid)}`
      )
      return res.data as FimAssetInstanceEntry
    },
    [authorizedAxios, siteBase]
  )

  /** POST /sites/{site}/instances/{subject_uid}/refill — re-queue the fill. */
  const refillInstance = useCallback(
    async (
      site: string,
      subjectUid: string
    ): Promise<AssetInstanceActionResult> => {
      const res = await authorizedAxios.post(
        `${siteBase(site)}/instances/${encodeURIComponent(subjectUid)}/refill`
      )
      return res.data as AssetInstanceActionResult
    },
    [authorizedAxios, siteBase]
  )

  /**
   * POST /sites/{site}/evaluation/source-list-compare — score the candidate
   * docnav_bbox list against the ground-truth one. Symmetric computation:
   * which list is "ground truth" is this request's designation, so swapping
   * the two ids swaps recall and precision. Computed on demand; nothing is
   * persisted server-side.
   */
  const compareSourceLists = useCallback(
    async (
      req: CompareSourceListsRequest
    ): Promise<CompareSourceListsResponse> => {
      const res = await authorizedAxios.post(
        `${siteBase(req.site)}/evaluation/source-list-compare`,
        {
          gt_source_list_id: req.gtSourceListId,
          candidate_source_list_id: req.candidateSourceListId,
          ...(req.boxIouThreshold !== undefined && {
            box_iou_threshold: req.boxIouThreshold,
          }),
          ...(req.boxProximityCap !== undefined && {
            box_proximity_cap: req.boxProximityCap,
          }),
          ...(req.includeCitations !== undefined && {
            include_citations: req.includeCitations,
          }),
          ...(req.sharedDocumentsOnly !== undefined && {
            shared_documents_only: req.sharedDocumentsOnly,
          }),
          ...(req.maxAliasDetail !== undefined && {
            max_alias_detail: req.maxAliasDetail,
          }),
        }
      )
      return res.data as CompareSourceListsResponse
    },
    [authorizedAxios, siteBase]
  )

  /**
   * DELETE /sites/{site}/sources/{id} — remove a source list + its source
   * records (FIM store only; the upstream source system is untouched).
   */
  const deleteSourceList = useCallback(
    async (id: string, site?: string): Promise<DeleteSourceListResult> => {
      const res = await authorizedAxios.delete(
        `${siteBase(site)}/sources/${encodeURIComponent(id)}`
      )
      return res.data as DeleteSourceListResult
    },
    [authorizedAxios, siteBase]
  )

  return {
    baseURL,
    compareSourceLists,
    createSourceList,
    runFusion,
    runMatching,
    submitReview,
    instantiateSubject,
    refillInstance,
    listInstances,
    getInstance,
    validateSubject,
    reclassifySubject,
    suppressSubjectInput,
    previewSubjectDecision,
    commitSubjectDecision,
    searchSubjectDecisionCandidates,
    getSubjectDecisionRevertContext,
    getSiteConfig,
    getCorpora,
    deleteSourceList,
  }
}
